import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLoader from './AppLoader';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresCompany?: boolean;
  requiresSubscription?: boolean;
  requireEmailVerified?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiresCompany = true,
  requiresSubscription = true,
  requireEmailVerified = true,
}) => {
  const { user, company, loading, userCompanies } = useAuth();

  if (loading) {
    return <AppLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireEmailVerified && !user.isEmailVerified) {
    return <Navigate to="/verify-email-pending" replace />;
  }

  const hasCompanies = userCompanies.length > 0;

  // Redirect to company setup if a company is required but the user has none
  if (requiresCompany && !hasCompanies) {
    return <Navigate to="/onboarding/company" replace />;
  }

  if (requiresSubscription && !company?.subscription?.tier) {
    return (
      <Navigate
        to={
          company?.subscription?.hasSubscribed
            ? '/subscription-expired'
            : '/onboarding/subscription'
        }
        replace
      />
    );
  }

  if (requiresSubscription && !company?.subscription?.isActive) {
    return <Navigate to="/subscription-expired" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
